import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Package, Truck, PlayCircle, FileText, HelpCircle, MessageSquare, CheckCircle2, Clock, MapPin, Send, ExternalLink, Home } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerPortalPage() {
  const { orderId } = useParams();
  const [portalData, setPortalData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketMessage, setTicketMessage] = useState("");
  const [ticketPriority, setTicketPriority] = useState("normal");
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);

  const fetchPortalData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/portal/order/${orderId}`);
      setPortalData(response.data);
      setError(null);
    } catch (err) {
      setError("Order not found or access denied");
    } finally {
      setIsLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (orderId) fetchPortalData();
  }, [orderId, fetchPortalData]);

  const submitSupportTicket = async () => {
    if (!ticketSubject || !ticketMessage) {
      toast.error("Please fill in all fields");
      return;
    }
    setIsSubmittingTicket(true);
    try {
      await axios.post(`${API}/portal/support`, {
        order_id: orderId,
        customer_email: portalData.order.email,
        subject: ticketSubject,
        message: ticketMessage,
        priority: ticketPriority
      });
      toast.success("Support ticket submitted");
      setTicketSubject("");
      setTicketMessage("");
      fetchPortalData();
    } catch (err) {
      toast.error("Failed to submit ticket");
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <p className="text-zinc-600">Loading your portal...</p>
      </div>
    );
  }

  if (error || !portalData) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-white rounded-sm border border-zinc-200 p-8 text-center">
          <Package className="h-16 w-16 text-zinc-300 mx-auto mb-6" />
          <h1 className="font-heading text-2xl font-bold uppercase text-zinc-950 mb-4">Portal Access Error</h1>
          <p className="text-zinc-600 mb-6">{error}</p>
          <Link to="/"><Button className="bg-blue-600 hover:bg-blue-700"><Home className="h-4 w-4 mr-2" /> Home</Button></Link>
        </div>
      </div>
    );
  }

  const order = portalData.order;
  const resources = portalData.resources || [];
  const freight = portalData.freight;
  const tickets = portalData.tickets || [];

  const groupedResources = resources.reduce((acc, r) => {
    if (!acc[r.category]) acc[r.category] = [];
    acc[r.category].push(r);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-zinc-950 text-white py-6 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm uppercase mb-1">Customer Portal</p>
              <h1 className="font-heading text-3xl font-bold uppercase">Order {order.order_number}</h1>
            </div>
            <Badge className="bg-blue-600 text-white text-sm uppercase">{order.status}</Badge>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white border border-zinc-200">
            <TabsTrigger value="overview" className="font-heading uppercase text-xs">Overview</TabsTrigger>
            <TabsTrigger value="tracking" className="font-heading uppercase text-xs">Tracking</TabsTrigger>
            <TabsTrigger value="resources" className="font-heading uppercase text-xs">Resources</TabsTrigger>
            <TabsTrigger value="support" className="font-heading uppercase text-xs">Support</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-sm border border-zinc-200 p-6">
                <h3 className="font-heading text-lg uppercase text-zinc-600 mb-4">Order Details</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-zinc-500">Order #</span><span className="font-mono">{order.order_number}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Customer</span><span>{order.customer_name}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Email</span><span>{order.email}</span></div>
                </div>
              </div>
              <div className="bg-white rounded-sm border border-zinc-200 p-6">
                <h3 className="font-heading text-lg uppercase text-zinc-600 mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  {freight?.tracking_number && (
                    <Link to={`/tracking/${freight.tracking_number}`} className="block">
                      <Button variant="outline" className="w-full justify-start"><Truck className="h-4 w-4 mr-2" /> Track Shipment</Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-sm border border-zinc-200 p-6 mt-6">
              <h3 className="font-heading text-lg uppercase text-zinc-600 mb-6">Order Progress</h3>
              <div className="flex justify-between items-center">
                {["Payment", "Production", "QC", "Shipped", "Delivered"].map((step, idx) => {
                  const stages = { Payment: ["paid","in_production","qc_complete","shipped","delivered"], Production: ["in_production","qc_complete","shipped","delivered"], QC: ["qc_complete","shipped","delivered"], Shipped: ["shipped","delivered"], Delivered: ["delivered"] };
                  const done = stages[step].includes(order.status);
                  return (
                    <div key={step} className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${done ? 'bg-green-600 text-white' : 'bg-zinc-200 text-zinc-500'}`}>
                        {done ? <CheckCircle2 className="h-5 w-5" /> : idx + 1}
                      </div>
                      <p className={`text-xs mt-2 ${done ? 'text-green-600 font-medium' : 'text-zinc-500'}`}>{step}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="tracking">
            {freight ? (
              <div className="bg-white rounded-sm border border-zinc-200 p-6">
                <h3 className="font-heading text-lg uppercase mb-6">Shipment Tracking</h3>
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div><p className="text-xs text-zinc-500 uppercase">Tracking #</p><p className="font-mono">{freight.tracking_number || "Pending"}</p></div>
                  <div><p className="text-xs text-zinc-500 uppercase">Carrier</p><p>{freight.carrier || "TBD"}</p></div>
                  <div><p className="text-xs text-zinc-500 uppercase">Est. Delivery</p><p>{freight.estimated_days ? `${freight.estimated_days} days` : "TBD"}</p></div>
                </div>
                <div className="border-t border-zinc-200 pt-6">
                  <div className="flex items-start gap-4 mb-4">
                    <MapPin className="h-5 w-5 text-zinc-400 mt-1" />
                    <div><p className="text-sm text-zinc-500">From</p><p className="font-medium">{freight.origin}</p></div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin className="h-5 w-5 text-blue-600 mt-1" />
                    <div><p className="text-sm text-zinc-500">To</p><p className="font-medium">{freight.destination}</p></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-sm border border-zinc-200 p-12 text-center">
                <Truck className="h-12 w-12 text-zinc-300 mx-auto mb-4" />
                <h3 className="font-heading text-lg uppercase text-zinc-600 mb-2">No Shipping Info Yet</h3>
                <p className="text-sm text-zinc-500">Tracking will appear once your order ships.</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="resources">
            {Object.keys(groupedResources).length === 0 ? (
              <div className="bg-white rounded-sm border border-zinc-200 p-12 text-center">
                <FileText className="h-12 w-12 text-zinc-300 mx-auto mb-4" />
                <h3 className="font-heading text-lg uppercase text-zinc-600 mb-2">Resources Coming Soon</h3>
                <p className="text-sm text-zinc-500">Guides and manuals will be added here.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {Object.entries(groupedResources).map(([category, items]) => (
                  <div key={category} className="bg-white rounded-sm border border-zinc-200 p-6">
                    <h3 className="font-heading uppercase text-sm text-zinc-600 mb-4 capitalize">{category}</h3>
                    <div className="space-y-3">
                      {items.map((item) => (
                        <a key={item.id} href={item.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-3 bg-zinc-50 rounded-sm hover:bg-zinc-100">
                          <span className="text-sm font-medium">{item.title}</span>
                          <ExternalLink className="h-4 w-4 text-zinc-400" />
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="support">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-sm border border-zinc-200 p-6">
                <h3 className="font-heading text-lg uppercase text-zinc-600 mb-4">Create New Ticket</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase">Subject</Label>
                    <Input value={ticketSubject} onChange={(e) => setTicketSubject(e.target.value)} placeholder="Brief description" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase">Priority</Label>
                    <Select value={ticketPriority} onValueChange={setTicketPriority}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase">Message</Label>
                    <Textarea value={ticketMessage} onChange={(e) => setTicketMessage(e.target.value)} placeholder="Describe your issue..." rows={4} className="mt-1" />
                  </div>
                  <Button onClick={submitSupportTicket} disabled={isSubmittingTicket} className="w-full bg-blue-600 hover:bg-blue-700">
                    {isSubmittingTicket ? "Submitting..." : <><Send className="h-4 w-4 mr-2" /> Submit</>}
                  </Button>
                </div>
              </div>

              <div className="bg-white rounded-sm border border-zinc-200 p-6">
                <h3 className="font-heading text-lg uppercase text-zinc-600 mb-4">Your Tickets</h3>
                {tickets.length === 0 ? (
                  <p className="text-sm text-zinc-500">No tickets yet.</p>
                ) : (
                  <div className="space-y-4">
                    {tickets.map((ticket) => (
                      <div key={ticket.id} className="p-4 bg-zinc-50 rounded-sm">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium text-sm">{ticket.subject}</p>
                          <Badge className={ticket.status === "resolved" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}>{ticket.status}</Badge>
                        </div>
                        <p className="text-xs text-zinc-500">{ticket.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
